import { useState } from 'react'
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Replace, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

export type PlacementDirection = 'top' | 'bottom' | 'left' | 'right' | 'replace' | 'tab'

interface PlacementOverlayProps {
  panelId: string
  onPlace: (direction: PlacementDirection) => void
  /** Show the Replace action (default true). Duplicate-initiated placements
   *  hide it: replacing a panel with a copy of itself is a no-op. */
  showReplace?: boolean
}

const BUTTONS: {
  direction: PlacementDirection
  Icon: React.ElementType
  className: string
}[] = [
  { direction: 'top', Icon: ChevronUp, className: '-top-px left-1/2 -translate-x-1/2' },
  { direction: 'bottom', Icon: ChevronDown, className: '-bottom-px left-1/2 -translate-x-1/2' },
  { direction: 'left', Icon: ChevronLeft, className: 'top-1/2 -left-px -translate-y-1/2' },
  { direction: 'right', Icon: ChevronRight, className: 'top-1/2 -right-px -translate-y-1/2' },
]

export function PlacementOverlay({
  panelId: _panelId,
  onPlace,
  showReplace = true,
}: PlacementOverlayProps) {
  const [hovered, setHovered] = useState(false)

  return (
    <div
      className="absolute inset-0 z-50 flex items-center justify-center transition-colors"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Scrim — darker on hover */}
      <div
        className={cn(
          'absolute inset-0 transition-opacity',
          hovered ? 'bg-background/85' : 'bg-background/50',
        )}
      />

      {/* D-pad + replace — only visible when hovering this panel */}
      {hovered && (
        <div className="relative flex flex-col items-center gap-5">
          <div className="relative h-[120px] w-[120px]">
            {BUTTONS.map(({ direction, Icon, className }) => (
              <button
                key={direction}
                onClick={() => onPlace(direction)}
                className={cn(
                  'absolute flex h-9 w-9 cursor-pointer items-center justify-center rounded-md bg-primary text-primary-foreground shadow-md transition-all hover:bg-primary hover:scale-110',
                  className,
                )}
                aria-label={`Place panel ${direction}`}
              >
                <Icon className="h-5 w-5" />
              </button>
            ))}
            {/* Center = add as a tab within this pane (edges split, center tabs). */}
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => onPlace('tab')}
                    className="absolute left-1/2 top-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md border border-primary-foreground/30 bg-primary text-primary-foreground shadow-md transition-all hover:bg-primary hover:scale-110"
                    aria-label="Add panel as a tab"
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Add Tab to Panel</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {showReplace && (
            <button
              onClick={() => onPlace('replace')}
              className="flex h-8 cursor-pointer items-center gap-1.5 rounded-md border border-foreground/20 bg-foreground/15 px-4 text-sm font-medium text-foreground shadow-lg transition-all hover:bg-foreground/25 hover:scale-105"
              aria-label="Replace this panel"
            >
              <Replace className="h-3.5 w-3.5" />
              Replace
            </button>
          )}
        </div>
      )}
    </div>
  )
}
